import { useEffect } from 'react'
import { Mic, MicOff, PhoneOff, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import useVoiceClient from '@/hooks/useVoiceClient'

const ETIQUETA_ESTADO = {
  disconnected: 'Sin conectar',
  connecting: 'Conectando…',
  connected: 'Conectado',
  listening: 'Escuchando…',
  speaking: 'Hablando…',
  error: 'Error',
}

function mmss(segundos) {
  const m = Math.floor(segundos / 60)
  const s = segundos % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

/**
 * MODO VOZ del chat de migración — mismo backend de estado (`hoja.chat`) que el modo texto,
 * distinto transporte (WebSocket + Gemini Live en vez de HTTP). El `agentConfig.context`
 * lleva el mismo `proyectoId`/`hojaId` que ya usa el modo texto: las tools de voz
 * (`proponer_productos`, etc.) escriben en la MISMA hoja, así que cambiar de texto a voz a
 * medio catálogo no pierde nada.
 */
export default function VoicePanelMigracion({ proyectoId, hojaId, onCatalogoActualizado }) {
  const token = typeof window !== 'undefined' ? localStorage.getItem('partnerToken') : null
  const consultorId = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('partnerUser') || 'null')?.id : null

  const agentConfig = {
    name: 'Agente de migración',
    role: 'Cd2bMigracionChat',
    voiceName: 'Kore',
    moduleCode: 'cd2b-migracion-voz',
    context: { proyectoId, hojaId, consultorId },
  }

  const { state, error, toolExecutions, isMuted, callDuration, startCall, endCall, toggleMute } = useVoiceClient({ token, agentConfig })

  // Cada vez que una tool de catálogo termina, el backend ya guardó el cambio en hoja.chat —
  // se le avisa al padre para que recargue el estado (mismo patrón que chatEstadoProyecto).
  useEffect(() => {
    const ultima = toolExecutions[toolExecutions.length - 1]
    if (ultima?.result !== undefined && ['proponer_productos', 'sugerir_precios_mercado', 'confirmar_catalogo'].includes(ultima.name)) {
      onCatalogoActualizado?.()
    }
  }, [toolExecutions, onCatalogoActualizado])

  const enLlamada = state !== 'disconnected' && state !== 'error'

  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-6">
        <div
          className={cn(
            'flex h-16 w-16 items-center justify-center rounded-full transition-colors',
            state === 'speaking' && 'bg-primary-100 dark:bg-primary-900/40 text-primary animate-pulse',
            state === 'listening' && 'bg-success-100 dark:bg-success/15 text-success-700 dark:text-success-300',
            state === 'connecting' && 'bg-neutral-100 dark:bg-neutral-800 text-neutral-400 dark:text-neutral-500',
            (state === 'disconnected' || state === 'error') && 'bg-neutral-100 dark:bg-neutral-800 text-neutral-400 dark:text-neutral-500',
            state === 'connected' && 'bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400'
          )}
        >
          {state === 'connecting' ? <Loader2 size={24} className="animate-spin" /> : <Mic size={24} />}
        </div>

        <p className="text-body-sm font-medium text-neutral-700 dark:text-neutral-300">{ETIQUETA_ESTADO[state] || state}</p>
        {enLlamada && <p className="text-caption tabular-nums text-neutral-400 dark:text-neutral-500">{mmss(callDuration)}</p>}
        {error && <p className="max-w-xs text-center text-caption text-danger-600 dark:text-danger-300">{error}</p>}

        <div className="flex items-center gap-2">
          {enLlamada ? (
            <>
              <Button variant="outline" size="icon-sm" onClick={toggleMute} title={isMuted ? 'Reactivar micrófono' : 'Silenciar'}>
                {isMuted ? <MicOff size={16} /> : <Mic size={16} />}
              </Button>
              <Button variant="destructive" size="icon-sm" onClick={endCall} title="Colgar">
                <PhoneOff size={16} />
              </Button>
            </>
          ) : (
            <Button onClick={startCall} disabled={!proyectoId}>
              <Mic size={15} className="mr-1.5" /> Llamar al agente
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
